import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import HistoryIcon from '@mui/icons-material/History';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Badge,
  Box,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import CreateModal from '@bumble/app/components/CreateModal';
import SplitPanes from '@bumble/app/components/SplitPanes';
import { Indicator } from '@bumble/app/components/Indicator';
import { NDCreate, EditNDInfo, NDSessions, NDConnect } from '@bumble/app/components/network-devices';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { apiError } from '@bumble/app/utils/api-error';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { NetworkDeviceOnDiagPortWithSessions } from '@bumble/app/__generated__/data-contracts';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import ConsoleAccessHistory from '@bumble/app/components/ConsoleAccessHistory';
import { ConsoleHistoryCpe } from '@bumble/app/utils/console-access-history';

export const NetworkAccess = () => {
  const api = useApi();

  const infoURL = 'https://www.bumblebeenet.com/post/oob-remote-network-based-access';

  const [tableData, setTableData] = useState<NetworkDeviceOnDiagPortWithSessions[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [cpes, setCpes] = useState<any[]>([{ label: 'Select a CPE…', value: 'none'}]);
  const [selectedCpe, setSelectedCpe] = useState<any>({ label: 'Select a CPE…', value: 'none'});
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showConnectDialog, setShowConnectDialog] = useState<boolean>(false);
  const [historySelection, setHistorySelection] = useState<{
    connection: ConsoleHistoryCpe;
    label: string;
  } | null>(null);
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<{
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    hasDivider?: boolean;
    disabled?: boolean;
  }[]>([]);

  const { data: cpeData } = useQuery(
    ['routers'],
    api.getCpeApiV1GetCpeGet.bind(null, {}),
    { enabled: true }
  );

  const {
    isLoading: loadingNetDevice,
    data: netDeviceData,
    refetch: netDeviceRefetch,
    isRefetching: refetchingNetDevice,
  } = useQuery(
    ['all-network-devices', selectedCpe?.value],
    () => {
      return api.getAllNetworkDevicesApiV1GetAllNetworkDevicesGet({
        node_id: selectedCpe.value,
      });
    },
    {
      enabled: !!selectedCpe?.value && selectedCpe.value !== 'none',
      retry: false,
      onError: (error: any) => {
        if (error.message !== 'No CPE selected') {
          apiError(error);
        }
      },
    }
  );

  const discoverPost = useMutation(
    api.discoverNetworkDeviceApiV1DiscoverNetworkDevicePost,
    {
      onSuccess: () => {
        toast.success('Device discovery completed successfully.');
        handleDataRefetch();
      },
      onError: apiError,
    }
  );

  const removeND = useMutation(
    api.removeNetworkDeviceApiV1RemoveNetworkDevicePost,
    {
      onSuccess() {
        toast.success('Device removed successfully.');
        setLoadingModalOpen(false);
        setShowConfirmDelete(false);
        handleDataRefetch();
        setRowSelectionModel([]);
      },
      onError(error: any) {
        apiError(error);
        setLoadingModalOpen(false);
        setShowConfirmDelete(false);
      },
    }
  );

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const selectedND = useMemo(() => {
    if (!selectedRowItem || !tableData) return undefined;
    return tableData.find((nd: any) => nd.mac_addr === selectedRowItem);
  }, [selectedRowItem, tableData]);

  useEffect(() => {
    const entity_data = cpeData?.data;
    let actualEntities = [{ label: 'Select a CPE…', value: 'none' }];
    if (entity_data?.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: any) => ({
        label: e.cpe_name,
        value: e.cpe_id,
      })));
    }
    setCpes(actualEntities);
    if (!selectedCpe || !actualEntities.find(c => c.value === selectedCpe?.value)) {
      setSelectedCpe(actualEntities[0]);
      setTableData(
        netDeviceData?.data?.map((item: any) => ({
          ...item,
          id: item.mac_addr,
        })) || []
      );
    }
  }, [cpeData]);

  useEffect(() => {
    if (selectedCpe) {
      setTableData(
        netDeviceData?.data?.map((item: any) => ({
          ...item,
          id: item.mac_addr,
        })) || []
      );
    }
  }, [netDeviceData]);

  const columns: GridColDef[] = [
    {
      field: 'device_name',
      headerName: 'Device Name',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'reachable',
      headerName: 'Status (Reachable)',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {params.value !== undefined ? <Indicator active={params.value === true} /> : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'ip_addr',
      headerName: 'IP Address',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'vendor_name',
      headerName: 'Manufacturer',
      width: 250,
      disableColumnMenu: true,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      disableColumnMenu: true,
    },
  ];

  const changeCpe = (val: any) => {
    setSelectedCpe(val);
    setTableData(
      netDeviceData?.data?.map((item: any) => ({
        ...item,
        id: item.mac_addr,
      })) || []
    );
    setRowSelectionModel([]);
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
    if (newSelection.slice(-1)[0] !== rowSelectionModel[0]) setHistorySelection(null);
  };

  useEffect(() => {
    setHistorySelection(null);
  }, [selectedCpe?.value]);

  useEffect(() => {
    const items = [
      {
        label: 'Edit Device Name',
        icon: <EditIcon color="info" fontSize="small" />,
        onClick: () => { setShowEditModal(true); },
      },
      {
        label: 'New Connection',
        icon: <LinkIcon color="info" fontSize="small" />,
        onClick: () => { setShowConnectDialog(true); },
        hasDivider: true,
      },
      {
        label: 'Delete',
        icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
        onClick: () => { setShowConfirmDelete(true); },
        hasDivider: true,
      },
      {
        label: 'History',
        icon: <HistoryIcon color="info" fontSize="small" />,
        onClick: () => {
          setHistorySelection({
            connection: { cpe_id: selectedCpe.value, access_type: 'eth' },
            label: `${selectedCpe.label} — All network access`,
          });
        },
        disabled: !selectedND,
      },
    ];
    setMenuItems([...items]);
  }, [selectedRowItem, selectedND, selectedCpe?.value, selectedCpe?.label]);

  const handleDataRefetch = () => {
    netDeviceRefetch();
    toast.success('Refreshed the list of network devices.');
  };

  const handleCreateModalOpen = () => {
    if (!selectedCpe || selectedCpe.value === 'none') {
      toast.error('Please select a CPE first');
      return;
    }
    setOpenCreateModal(true);
  };

  const handleDiscover = () => {
    discoverPost.mutate({ node_id: selectedCpe.value });
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  const handleConnectModalClose = () => {
    setShowConnectDialog(false);
    handleDataRefetch();
  };

  const handleNDDelete = () => {
    setLoadingModalOpen(true);
    removeND.mutate({
      node_id: selectedCpe.value,
      mac_addr: selectedRowItem
    });
  };

  return (
    <PageContent>
      <PageHeader pageName="Network-Based Access" />
      <Box sx={{ display: 'inline-flex', mb: 5, position: 'relative', gap: 2 }}>
        <Select options={cpes} value={selectedCpe} onChange={changeCpe} styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: '8px',
            width: '300px',
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            width: '300px',
          }),
        }}/>
        <Badge badgeContent={0} color="secondary">
          <Box
            component="a"
            href={infoURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Tooltip arrow title="Info">
              <IconButton color="primary" size="small">
                <InfoOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Badge>
      </Box>
      {loadingNetDevice && selectedCpe.value !== 'none' ? (
        <Loading />
      ) : (
        <SplitPanes
          defaultSizes={['30%', '70%']}
          panePadding={8}
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={tableData}
                  columns={columns}
                  pagination
                  rowSelectionModel={rowSelectionModel}
                  handleSelectionChange={handleSelectionChange}
                  itemsSelected={rowSelectionModel.length === 1}
                  refreshData={handleDataRefetch}
                  isRefetching={refetchingNetDevice}
                  refreshDisabled={!selectedCpe || selectedCpe.value === 'none'}
                  newLabel="Add Device"
                  newOnClick={handleCreateModalOpen}
                  newDisabled={!selectedCpe || selectedCpe.value === 'none'}
                  extraBtnLabel="Discover Device"
                  extraBtnOnClick={handleDiscover}
                  extraBtnDisabled={!selectedCpe || selectedCpe.value === 'none' || discoverPost.isLoading}
                  menuItems={menuItems}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <NDSessions
                    data={selectedND}
                    onSessionUpdate={() => netDeviceRefetch()}
                    isRefetching={refetchingNetDevice}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {openCreateModal && selectedCpe && selectedCpe.value !== 'none' && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Add Device"
        >
          <NDCreate handleClose={handleCreateModalClose} nodeId={selectedCpe.value} />
        </CreateModal>
      )}
      {historySelection && (
        <ConsoleAccessHistory
          allHistory
          connection={historySelection.connection}
          label={historySelection.label}
          onClose={() => setHistorySelection(null)}
        />
      )}
      {showConnectDialog && (
        <CreateModal
          open={showConnectDialog}
          handleClose={handleConnectModalClose}
          title="Network Based Remote Access"
        >
          <NDConnect
            handleClose={handleConnectModalClose}
            cpeId={selectedCpe.value}
            macAddr={selectedRowItem}
            targetIP={selectedND?.ip_addr || ''}
          />
        </CreateModal>
      )}
      {showEditModal && (
        <DialogStandard
          open={showEditModal}
          handleClose={() => setShowEditModal(false)}
          title="Edit Device Name"
        >
          <EditNDInfo
            onSubmit={() => {
              setShowEditModal(false);
              netDeviceRefetch();
            }}
            cpeId={selectedCpe.value}
            macAddr={selectedRowItem}
            deviceName={selectedND?.device_name || ''}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleNDDelete}
          title="Delete Device?"
          description={`Are you certain you wish to proceed with deleting ${selectedND?.device_name}? Please note that this action is irreversible and will result in the permanent removal of network device.`}
          confirmationPhrase={selectedND?.device_name}
        />
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
};
